import React, { useRef, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { SurfacePill, type PillAsset } from '../alt/SurfacePill';
import { domToSvg, preloadImages, svgToPng } from '../../lib/domToSvg';
import { download, downloadSvg, writeToClipboard } from './useExportFrames';

/**
 * THE LOCKUP GRID, SHARED BY EVERY EXPORT SURFACE.
 *
 * Lifted out of the screen-library page so the creator pack can use the same
 * thing. The pack originally shipped bare marks in a plain grid, which handed
 * over a logo but not the ASSET anyone actually places on a clip: a mark with
 * its name beside it, in the tone the footage needs. Two implementations of
 * that would have drifted the moment one was fixed.
 *
 * On the page these lockups sit inside a segmented control, so the mark and
 * word carry a cream track behind them and an ink fill when selected. Neither
 * belongs to the lockup, so both are dropped here: the artboard is genuinely
 * transparent, and the preview sits on a checkerboard to prove it.
 *
 * Four states per tool, from two switches: capsule or bare, for light footage
 * or dark. That is the whole reason this beats a static grid — a mark that
 * only exists in one tone is a mark you cannot use half the time.
 */
export function SurfacePills({
  heading,
  title,
  note,
  items
}: {
  heading: string;
  title: string;
  note: string;
  items: PillAsset[];
}) {
  const frames = useRef<Record<string, HTMLSpanElement | null>>({});
  const [tone, setTone] = useState<'ink' | 'white'>('ink');
  const [shape, setShape] = useState<'pill' | 'bare'>('pill');
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // Keyed by state as well as id: the same tool in two tones is two different
  // files, and two different nodes to capture.
  const key = (id: string) => `${id}-${tone}-${shape}`;
  const markup = async (id: string) => {
    const node = frames.current[key(id)];
    if (!node) return null;
    // A lockup is a logo beside a word, so an unloaded font or logo is not a
    // detail missing from the asset — it is the whole asset.
    if (document.fonts?.status !== 'loaded') await document.fonts.ready;
    await preloadImages(node);
    return await domToSvg(node, null);
  };
  const run = async (id: string, job: (svg: string) => void | Promise<void>) => {
    setBusy(key(id));
    try {
      const svg = await markup(id);
      if (svg) await job(svg);
    } finally {
      setBusy(b => b === key(id) ? null : b);
    }
  };
  return <section data-id="element-3344" data-mp="23mr3oojjdbx5">
      <p className="mb-5 border-b border-[#E7E0D4] pb-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-3345" data-mp="1ovrzrqtnbbjw">
        {heading}
      </p>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-3346" data-mp="1ttk1fck3ec1a">
        <div className="max-w-[62ch]" data-id="element-3347" data-mp="2hk7c25le7frj">
          <h2 className="font-title text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-3348" data-mp="2hj4lxzvw9hco">
            {title}
          </h2>
          <p className="mt-1 text-[13px] leading-relaxed text-neutral-500" data-id="element-3349" data-mp="aw716m0ap5be">
            {note}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-3350" data-mp="5xm06kd7yhlw">
          <div className="inline-flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" data-id="element-3351" data-mp="5uzpibv17lzg">
            {(['pill', 'bare'] as const).map(s => <button key={s} type="button" onClick={() => setShape(s)} aria-pressed={shape === s} className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${shape === s ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-3352" data-mp="fmne8507eox8">
                {s === 'pill' ? 'Capsule' : 'Bare'}
              </button>)}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" data-id="element-3353" data-mp="voap8e86qki">
            {(['ink', 'white'] as const).map(t => <button key={t} type="button" onClick={() => setTone(t)} aria-pressed={tone === t} className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${tone === t ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-3354" data-mp="1cc4tnboznpc4">
                {t === 'ink' ? 'For light' : 'For dark'}
              </button>)}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3355" data-mp="2cm63tnm7yh16">
        {items.map(p => {
        const busyNow = busy === key(p.id);
        const copiedNow = copied === key(p.id);
        return <div key={p.id} className="rounded-2xl border border-[#E7E0D4] bg-white p-4" onPointerEnter={() => {
          const node = frames.current[key(p.id)];
          if (node) void preloadImages(node);
        }} data-id="element-3356" data-mp="2ml52qeh6myaz">
              {/* Checkerboard, so transparency is visible rather than claimed. */}
              <div className="flex h-[92px] items-center justify-center rounded-xl" style={{
            backgroundColor: tone === 'white' ? '#2B211A' : '#FFFFFF',
            backgroundImage: tone === 'white' ? undefined : 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
            backgroundSize: '14px 14px',
            backgroundPosition: '0 0, 7px 7px'
          }} data-id="element-3357" data-mp="30c9uyvi80oba">
                <span ref={el => {
              frames.current[key(p.id)] = el;
            }} className="inline-flex" data-id="element-3358" data-mp="ooevjf0tm0d9">
                  <SurfacePill logo={p.logo} label={p.label} tone={tone} shape={shape} size={26} data-id="element-3359" data-mp="jqn22gkj266y" />
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2" data-id="element-3360" data-mp="1hfewti7roq18">
                <button type="button" disabled={busyNow} onClick={() => run(p.id, async svg => {
              download(await svgToPng(svg), `giga-${p.id}-${shape}-${tone}.png`);
            })} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#2B211A] px-3 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-3361" data-mp="1cfagbs6uzvb0">
                  <Download size={13} data-id="element-3362" data-mp="1vcv4ex1supuk" />
                  {busyNow ? 'Preparing…' : 'PNG'}
                </button>
                <button type="button" disabled={busyNow} onClick={() => run(p.id, svg => downloadSvg(svg, `giga-${p.id}-${shape}-${tone}.svg`))} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-3363" data-mp="3pm0ei7bhk3o1">
                  <Download size={13} data-id="element-3364" data-mp="36upkviedlcsg" />
                  SVG
                </button>
                <button type="button" disabled={busyNow} onClick={() => run(p.id, async svg => {
              await writeToClipboard(svg);
              setCopied(key(p.id));
              setTimeout(() => setCopied(c => c === key(p.id) ? null : c), 2000);
            })} className="inline-flex items-center justify-center rounded-lg border border-[#D8CFC2] bg-white px-2.5 py-2 text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" aria-label={`Copy ${p.label} lockup for Figma`} data-id="element-3365" data-mp="3aw2t1z4xxs2j">
                  {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-3366" data-mp="2klixg21nm4rx" /> : <Copy size={13} data-id="element-3367" data-mp="2kmbsw6v5x7ur" />}
                </button>
              </div>
            </div>;
      })}
      </div>
    </section>;
}